import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import TButton from "@/components/TButton";
import { FileDown, FileText, Loader2, FilePieChart } from "lucide-react";
import { toast } from "sonner";

const Reports = () => {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/executive");
      setReport(data);
      toast.success("Executive report generated");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { generate(); /* eslint-disable-next-line */ }, []);

  const downloadBoardPack = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get("/reports/board-pack", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `cyberspherex-board-pack-${Date.now()}.pdf`;
      a.click();
      toast.success("Board Pack PDF ready");
    } catch (e) {
      toast.error("PDF generation failed");
    } finally {
      setPdfBusy(false);
    }
  };

  const downloadJSON = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `cyberspherex-report-${Date.now()}.json`;
    a.click();
  };

  const downloadHTML = () => {
    if (!report) return;
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>CyberSphere X Executive Report</title>
<style>body{font-family:JetBrains Mono,monospace;background:#020617;color:#f8fafc;padding:40px;max-width:900px;margin:auto}
h1{color:#06b6d4;letter-spacing:.1em}h2{color:#d946ef;margin-top:30px}.box{border:1px solid #1e293b;padding:16px;margin:12px 0}
.tag{display:inline-block;border:1px solid #06b6d4;color:#06b6d4;padding:2px 8px;font-size:11px;letter-spacing:.2em}</style></head>
<body><span class="tag">CYBERSPHERE X · EXECUTIVE REPORT</span>
<h1>Cyber Risk Posture Snapshot</h1>
<div class="box"><b>Generated:</b> ${report.generated_at}<br><b>By:</b> ${report.generated_by}</div>
<h2>Executive Summary</h2><p>${report.executive_summary}</p>
<h2>Top Vulnerabilities</h2><ul>${report.top_vulnerabilities.map(v => `<li><b>${v.cve}</b> · ${v.title} · CVSS ${v.cvss} · ${v.asset}</li>`).join("")}</ul>
<h2>Recent Incidents</h2><ul>${report.recent_incidents.map(i => `<li>${i.severity.toUpperCase()} · ${i.title}</li>`).join("")}</ul>
<h2>Compliance</h2><ul>${report.overview.compliance.map(c => `<li>${c.framework}: ${c.coverage}%</li>`).join("")}</ul>
</body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `cyberspherex-report-${Date.now()}.html`;
    a.click();
  };

  return (
    <div className="p-6 max-w-5xl">
      <PageHeader
        tag="Executive / Reports"
        title="Executive Risk Report"
        accent="pink"
        subtitle="One-click board-ready cyber risk snapshot. Export JSON / HTML."
        actions={
          <div className="flex gap-2 flex-wrap">
            <TButton data-testid="regen-report" icon={busy ? Loader2 : FileText} onClick={generate} disabled={busy}>
              {busy ? "Generating" : "Regenerate"}
            </TButton>
            <TButton data-testid="download-json" icon={FileDown} variant="ghost" onClick={downloadJSON} disabled={!report}>JSON</TButton>
            <TButton data-testid="download-html" icon={FileDown} variant="ghost" onClick={downloadHTML} disabled={!report}>HTML</TButton>
            <TButton data-testid="download-board-pack" icon={pdfBusy ? Loader2 : FilePieChart} variant="accent" onClick={downloadBoardPack} disabled={pdfBusy}>
              {pdfBusy ? "Building" : "Board Pack PDF"}
            </TButton>
          </div>
        }
      />

      {!report ? (
        <div className="csx-card p-8 text-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="csx-card p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-400">Executive Summary</div>
            <p className="text-sm leading-relaxed text-slate-200 mt-3">{report.executive_summary}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ["Threat Score", report.overview.threat_score, "rose"],
              ["Assets", report.overview.assets_total, "cyan"],
              ["Vulnerabilities", report.overview.vulns_total, "fuchsia"],
              ["Open Incidents", report.overview.open_incidents, "amber"],
            ].map(([k, v, c]) => (
              <div key={k} className="csx-card p-4">
                <div className={`font-display text-3xl font-black text-${c}-400`}>{v}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{k}</div>
              </div>
            ))}
          </div>

          <div className="csx-card p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-400 mb-3">Top Vulnerabilities</div>
            <ul className="space-y-2 text-sm">
              {report.top_vulnerabilities.map((v) => (
                <li key={v.id} className="flex items-center justify-between border-b border-slate-800 pb-1.5">
                  <div>
                    <span className="font-mono text-cyan-300">{v.cve}</span>
                    <span className="text-slate-300 ml-2">{v.title}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-slate-500">{v.asset}</span>
                    <span className={`uppercase tracking-widest text-${v.severity === "critical" ? "rose" : v.severity === "high" ? "amber" : "slate"}-300`}>{v.severity}</span>
                    <span className="font-mono text-fuchsia-300">{v.cvss}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
